// Pure journal selection for veterinary attests.
// See webapp/src/components/dyrejournal/attestLayout.js (the renderer) and
// webapp/src/components/file/listTable.js (which loads the animal's journals).
//
// An attest (dødsattest, avlivningsattest, forsikringsattest, ...) is printed for a
// single animal. The relevant journal — e.g. the euthanasia journal for a dødsattest —
// may live on the CURRENT sale (newSale.journals) OR on a previous/closed sale that has
// been loaded separately (attestJournals). Looking only at the current sale meant the
// journal fields rendered "Ikke registrert i journal" whenever the attest was printed
// outside the original visit (deceased animal, fresh sale, etc.), even though the journal
// existed. This helper resolves the best journal across all provided sources.

/**
 * Pick the most relevant journal for `animalId` across the given journal sources.
 *
 * @param {number|string} animalId - The animal the attest is printed for.
 * @param {Array<Array<object>>} sources - Journal arrays in priority order
 *        (highest priority first), e.g. [newSale.journals, attestJournals].
 * @param {function(object): boolean} [hasContent] - Predicate that returns true when a
 *        journal actually carries data for the attest. Used to prefer a content-bearing
 *        journal over an empty placeholder one.
 * @returns {object} The chosen journal, or {} when none matches the animal.
 */
export function resolveAttestJournal(animalId, sources = [], hasContent = () => true) {
    const id = animalId != null && animalId !== '' ? parseInt(animalId, 10) : null;

    // Flatten the sources in priority order, de-duplicated by journal id.
    const seen = new Set();
    const ordered = [];
    (Array.isArray(sources) ? sources : []).forEach(list => {
        (Array.isArray(list) ? list : []).forEach(journal => {
            if (!journal || (journal.id != null && seen.has(journal.id))) {
                return;
            }
            if (journal.id != null) {
                seen.add(journal.id);
            }
            ordered.push(journal);
        });
    });

    // Restrict to the requested animal when we know it.
    const forAnimal = id != null ? ordered.filter(j => j.animal === id) : ordered;

    // Prefer (in priority order) a journal that actually carries attest content; otherwise
    // the first journal for the animal; otherwise nothing.
    return forAnimal.find(hasContent) || forAnimal[0] || {};
}

export default resolveAttestJournal;
